/**
 * ConfirmDialog - Action-focused confirmation dialog
 * 
 * Following Apple HIG principles:
 * - Buttons describe the action, not answer a question
 * - Destructive actions are visually distinct
 * - Clear, concise messaging
 * 
 * @example
 * // Delete action
 * <ConfirmDialog
 *   isOpen={showDelete}
 *   onClose={() => setShowDelete(false)}
 *   title="Delete Item"
 *   message="This will permanently remove this item from your stash."
 *   cancelLabel="Cancel"
 *   confirmLabel="Delete"
 *   confirmVariant="danger"
 *   onConfirm={handleDelete}
 * />
 * 
 * // Discard changes
 * <ConfirmDialog
 *   isOpen={showDiscard}
 *   onClose={() => setShowDiscard(false)}
 *   title="Discard Changes"
 *   message="You have unsaved changes that will be lost."
 *   cancelLabel="Keep Editing"
 *   confirmLabel="Discard"
 *   confirmVariant="warning"
 *   onConfirm={handleDiscard}
 * />
 */
import React, { useEffect } from 'react';
import { AlertTriangle, Trash2, LogOut, X } from 'lucide-react';

const CONFIRM_VARIANTS = {
  danger: {
    button: 'bg-red-500 text-white hover:bg-red-600',
    icon: <Trash2 size={20} className="text-red-500" />,
    iconBg: 'bg-red-100',
  },
  warning: {
    button: 'bg-amber-500 text-white hover:bg-amber-600',
    icon: <AlertTriangle size={20} className="text-amber-600" />,
    iconBg: 'bg-amber-100',
  },
  primary: {
    button: 'bg-sage text-white hover:bg-sage-dark',
    icon: null,
    iconBg: 'bg-sage/10',
  },
  logout: {
    button: 'bg-charcoal text-white hover:bg-charcoal/90',
    icon: <LogOut size={20} className="text-charcoal" />,
    iconBg: 'bg-gray-100',
  },
};

const ConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  cancelLabel = 'Cancel',
  confirmLabel = 'Confirm',   // Should describe the action: "Delete", "Remove", "Discard", etc.
  confirmVariant = 'danger',  // 'danger' | 'warning' | 'primary' | 'logout'
  isLoading = false,
  showIcon = true,
  testId = 'confirm-dialog',
}) => {
  // Handle Escape key to close dialog
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && !isLoading) {
        onClose?.();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, isLoading, onClose]);

  if (!isOpen) return null;

  const variant = CONFIRM_VARIANTS[confirmVariant] || CONFIRM_VARIANTS.danger;

  const handleConfirm = async () => {
    await onConfirm?.();
    onClose?.();
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose?.();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-charcoal/60 flex items-center justify-center z-[70] p-4"
      onClick={handleOverlayClick}
      data-testid={testId}
    >
      <div
        className="bg-white rounded-xl max-w-sm w-full shadow-2xl animate-in fade-in zoom-in duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex items-start gap-4">
            {/* Icon */}
            {showIcon && variant.icon && (
              <div className={`p-3 ${variant.iconBg} rounded-full flex-shrink-0`}>
                {variant.icon}
              </div>
            )}
            
            {/* Content */}
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-medium text-charcoal mb-2">
                {title || 'Confirm Action'}
              </h3>
              <p className="text-gray-soft text-sm leading-relaxed">
                {message}
              </p>
            </div>

            {/* Close button */}
            <button
              onClick={onClose}
              disabled={isLoading}
              className="text-gray-soft hover:text-charcoal transition-colors flex-shrink-0 disabled:opacity-50"
              data-testid={`${testId}-close`}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Action buttons */}
        <div className="px-6 pb-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 border border-beige text-charcoal rounded-lg hover:bg-cream transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid={`${testId}-cancel`}
          >
            {cancelLabel}
          </button>
          <button
            onClick={handleConfirm}
            disabled={isLoading}
            className={`px-4 py-2 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 ${variant.button}`}
            data-testid={`${testId}-confirm`}
          >
            {isLoading ? (
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              confirmLabel
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;

